import { MapUtils } from '../../../utility/map-utils.js';
import { VersionRange } from '../../core/version-range.js';
import {
  cloneListOfCloneable, DataModel, DataModelBuilder, isSameData,
  isSameList, SerializationContext, SerializedData,
} from '../../dto/dto.js';
import { InvoiceGroup, InvoiceScope } from './invoice-group.js';
import { Invoice } from './invoice.js';

export class OnlineInvoicingDetails extends DataModel {
  static readonly TYPE_ID: string = 'ce560f0f0a1844d4b7e2bec2fc830c95';
  static readonly SCHEMA_VERSION: number = 6;

  private constructor(
    private readonly entries: InvoiceGroup[],
  ) {
    super();
  }

  get $typeId() {
    return OnlineInvoicingDetails.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(OnlineInvoicingDetails.SCHEMA_VERSION);
  }

  static create({ entries }: { entries: InvoiceGroup[] }): OnlineInvoicingDetails {
    return new OnlineInvoicingDetails(entries);
  }

  static fromInvoices(options: {
    invoices: { invoiceGroupId: string, invoice: Invoice, scope: InvoiceScope }[],
  }): OnlineInvoicingDetails {
    const entries: InvoiceGroup[] = [];
    for(const entry of options.invoices) {
      entries.push(InvoiceGroup.create(entry.invoiceGroupId, entry.invoice, entry.scope));
    }

    return new OnlineInvoicingDetails(entries);
  }

  static fromLegacy(options: {
    orderInvoice: Invoice,
    applicationFeeInvoice: Invoice | null,
  }): OnlineInvoicingDetails {
    const entries: InvoiceGroup[] = [
      InvoiceGroup.forLegacyInvoice(options.orderInvoice, InvoiceScope.Order),
    ];

    if(options.applicationFeeInvoice != null) {
      entries.push(InvoiceGroup.forLegacyInvoice(options.applicationFeeInvoice, InvoiceScope.ApplicationFee));
    }

    return new OnlineInvoicingDetails(entries);
  }

  static fromMap(context: SerializationContext, map: SerializedData): OnlineInvoicingDetails {
    return new OnlineInvoicingDetails(
        MapUtils.getList(map, 'entries', (data) => {
          return context.fromData<InvoiceGroup>(data as SerializedData);
        }),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'entries': this.entries.map((e) => context.toData(e)),
    };
  }

  clone(): this {
    return new OnlineInvoicingDetails(
        cloneListOfCloneable(this.entries),
    ) as this;
  }

  isSame(other: OnlineInvoicingDetails): boolean {
    if(this === other) {
      return true;
    }

    return isSameList(other.entries, this.entries, isSameData);
  }

  hasAnyEntries(): boolean {
    return this.entries.length > 0;
  }

  hasAnyActiveEntries(): boolean {
    for(let i = 0; i < this.entries.length; i++) {
      if(this.entries[i].hasActiveInvoice()) {
        return true;
      }
    }

    return false;
  }

  hasAnyInvoiceForScope(scope: InvoiceScope): boolean {
    for(let i = 0; i < this.entries.length; i++) {
      if(this.entries[i].scope === scope) {
        return this.entries[i].hasAnyInvoice();
      }
    }

    return false;
  }

  tryFindActiveInvoiceForScope(scope: InvoiceScope): Invoice | null {
    for(let i = 0; i < this.entries.length; i++) {
      if(this.entries[i].scope === scope) {
        return this.entries[i].tryFindActiveInvoice();
      }
    }

    return null;
  }

  addEntry(entry: InvoiceGroup): void {
    this.entries.push(entry);
  }

  getInvoiceGroupByScope(scope: InvoiceScope): InvoiceGroup | null {
    for(let i = 0; i < this.entries.length; i++) {
      if(this.entries[i].scope === scope) {
        return this.entries[i];
      }
    }

    return null;
  }

  getInvoiceGroupById(groupId: string): InvoiceGroup | null {
    for(let i = 0; i < this.entries.length; i++) {
      if(this.entries[i].uniqueId === groupId) {
        return this.entries[i];
      }
    }

    return null;
  }

  getInvoicesForExport(): Invoice[] {
    const invoices: Invoice[] = [];
    for(const entry of this.entries) {
      invoices.push(...entry.getInvoicesForExport());
    }

    return invoices;
  }

  getInvoicesForCheckout(): Invoice[] {
    const invoices: Invoice[] = [];
    for(const entry of this.entries) {
      invoices.push(...entry.getInvoicesForCheckout());
    }

    return invoices;
  }
}

export class OnlineInvoicingDetailsBuilder extends DataModelBuilder<OnlineInvoicingDetails> {
  build(context: SerializationContext, map: SerializedData): OnlineInvoicingDetails {
    return OnlineInvoicingDetails.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(OnlineInvoicingDetails.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(OnlineInvoicingDetails.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version the [donation] field was added.
    if(version < 2) version = 2;

    /**
     * Version 3:
     * - change order from `InvoiceGroup` to `InvoiceGroup | null`.
     */
    if(version < 3) version = 3;

    /**
     * Version 4:
     * - add `supplement` field of type `InvoiceGroup | null`.
     */
    if(version < 4) version = 4;

    /**
     * Version 5:
     * - add `product` field of type `InvoiceGroup | null`.
     */
    if(version < 5) version = 5;

    /**
     * Version 6:
     * - add `entries` field of type `InvoiceGroup[]`.
     */
    if(version < 6) {
      const order = context.fromDataOrNull<InvoiceGroup>(MapUtils.getRecordOrNull(map, 'order'));
      const supplement = context.fromDataOrNull<InvoiceGroup>(MapUtils.getRecordOrNull(map, 'supplement'));
      const product = context.fromDataOrNull<InvoiceGroup>(MapUtils.getRecordOrNull(map, 'product'));
      const applicationFee = context.fromDataOrNull<InvoiceGroup>(MapUtils.getRecordOrNull(map, 'applicationFee'));
      const donation = context.fromDataOrNull<InvoiceGroup>(MapUtils.getRecordOrNull(map, 'donation'));

      const entries: InvoiceGroup[] = [];

      if(order != null) entries.push(order);
      if(supplement != null) entries.push(supplement);
      if(product != null) entries.push(product);
      if(applicationFee != null) entries.push(applicationFee);
      if(donation != null) entries.push(donation);

      map['entries'] = entries.map((e) => context.toData(e));
      version = 6;
    }


    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
